import React, { useState, useEffect, useCallback } from 'react';
import { X, Check, AlertTriangle, Save, Ban } from 'lucide-react';
import { Project, VWBAAnalysis, updateProject, associateFactSheetWithProject } from '../services/api';

interface FactsheetComparisonModalProps {
  extractedData: VWBAAnalysis;
  existingProject: Project;
  factSheetId: string;
  onClose: () => void;
  onResolved: () => void;
}

interface ComparisonField {
  key: string;
  name: string;
  factSheetValue: string;
  systemValue: string;
  match: boolean;
  editable: boolean;
}

const FactsheetComparisonModal: React.FC<FactsheetComparisonModalProps> = ({
  extractedData,
  existingProject,
  factSheetId,
  onClose,
  onResolved,
}) => {
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle escape key to close modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  // Extract values from extractedData
  const getExtractedValue = (key: string): string => {
    const data = extractedData.extractedData || {};
    if (key === 'name') return extractedData.projectName || '';
    if (key === 'basin') return data.basin || data.location || '';
    if (key === 'category') return data.category || '';
    if (key === 'volumetricWaterBenefit') return data.volumetricWaterBenefit || data.benefitVolume || '';
    if (key === 'costShare') return data.costShare || '';
    if (key === 'affectedFieldImpact') return data.affectedFieldImpact || '';
    if (key === 'quantificationMethod') return data.quantificationMethod || '';
    return '';
  };

  // Build comparison fields
  const fields: ComparisonField[] = [
    {
      key: 'basin',
      name: 'Basin',
      factSheetValue: getExtractedValue('basin'),
      systemValue: existingProject.basin || '',
      match:
        getExtractedValue('basin').toLowerCase() ===
        (existingProject.basin || '').toLowerCase(),
      editable: true,
    },
    {
      key: 'category',
      name: 'Category',
      factSheetValue: getExtractedValue('category'),
      systemValue: existingProject.category || '',
      match:
        getExtractedValue('category').toLowerCase() ===
        (existingProject.category || '').toLowerCase(),
      editable: true,
    },
    {
      key: 'volumetricWaterBenefit',
      name: 'Volumetric Water Benefit',
      factSheetValue: getExtractedValue('volumetricWaterBenefit'),
      systemValue: existingProject.volumetricWaterBenefit || '',
      match: getExtractedValue('volumetricWaterBenefit') === (existingProject.volumetricWaterBenefit || ''),
      editable: true,
    },
    {
      key: 'costShare',
      name: 'Cost-Share',
      factSheetValue: getExtractedValue('costShare'),
      systemValue: existingProject.costShare || '',
      match: getExtractedValue('costShare') === (existingProject.costShare || ''),
      editable: true,
    },
    {
      key: 'affectedFieldImpact',
      name: 'Affected Field Impact',
      factSheetValue: getExtractedValue('affectedFieldImpact'),
      systemValue: existingProject.affectedFieldImpact || '',
      match:
        getExtractedValue('affectedFieldImpact') ===
        (existingProject.affectedFieldImpact || ''),
      editable: true,
    },
    {
      key: 'quantificationMethod',
      name: 'Quantification Method',
      factSheetValue: getExtractedValue('quantificationMethod'),
      systemValue: existingProject.quantificationMethod || '',
      match:
        getExtractedValue('quantificationMethod') ===
        (existingProject.quantificationMethod || ''),
      editable: true,
    },
  ];

  const hasMismatch = fields.some((f) => !f.match);

  // Initialize edited values with factsheet values for mismatched fields
  useEffect(() => {
    const initialValues: Record<string, string> = {};
    fields.forEach((field) => {
      if (!field.match) {
        initialValues[field.key] = field.factSheetValue;
      }
    });
    setEditedValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValueChange = (key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleResolve = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build update payload with resolved values
      const updatePayload: Record<string, string | boolean> = {
        status: 'stage2',
        hasComparison: true,
      };

      // Add edited values to payload
      Object.entries(editedValues).forEach(([key, value]) => {
        if (value) {
          updatePayload[key] = value;
        }
      });

      // Update the project
      await updateProject(existingProject.id, updatePayload);

      // Associate the factsheet with the project
      await associateFactSheetWithProject(factSheetId, existingProject.id);

      onResolved();
    } catch (err) {
      console.error('Error resolving project:', err);
      setError('Failed to resolve inconsistencies. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkNonEligible = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await updateProject(existingProject.id, {
        status: 'non-eligible',
      });

      // Associate the factsheet with the project
      await associateFactSheetWithProject(factSheetId, existingProject.id);

      onResolved();
    } catch (err) {
      console.error('Error marking project as non-eligible:', err);
      setError('Failed to mark project as non-eligible. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="factsheet-comparison-modal-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2
              id="factsheet-comparison-modal-title"
              className="text-xl font-bold text-gray-900"
            >
              Matching Project Found
            </h2>
            <p className="text-gray-600 mt-1">
              A project with the same name and location already exists
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Project Name */}
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              <strong>Project:</strong> {existingProject.name}
            </p>
          </div>

          {/* Comparison Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Fact Sheet Column */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                New Factsheet (Extracted)
              </h3>
              <div className="space-y-3" role="list" aria-label="Factsheet values">
                {fields.map((field, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      field.match
                        ? 'border-green-200 bg-green-50'
                        : 'border-red-200 bg-red-50'
                    }`}
                    role="listitem"
                  >
                    <div className="text-xs text-gray-500 uppercase">{field.name}</div>
                    <div className="text-sm font-medium text-gray-900 mt-1">
                      {field.factSheetValue || '(not extracted)'}
                    </div>
                    <div
                      className={`flex items-center gap-1 mt-2 text-xs ${
                        field.match ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {field.match ? (
                        <Check className="w-3 h-3" aria-hidden="true" />
                      ) : (
                        <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                      )}
                      <span>{field.match ? 'Match' : 'Mismatch'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* System Column */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Existing Project Data
              </h3>
              <div className="space-y-3" role="list" aria-label="Existing project values">
                {fields.map((field, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      field.match
                        ? 'border-green-200 bg-green-50'
                        : 'border-red-200 bg-red-50'
                    }`}
                    role="listitem"
                  >
                    <div className="text-xs text-gray-500 uppercase">{field.name}</div>
                    {!field.match && field.editable ? (
                      <input
                        type="text"
                        value={editedValues[field.key] ?? field.systemValue}
                        onChange={(e) => handleValueChange(field.key, e.target.value)}
                        className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                        placeholder={`Enter ${field.name.toLowerCase()}`}
                      />
                    ) : (
                      <div className="text-sm font-medium text-gray-900 mt-1">
                        {field.systemValue || '(not set)'}
                      </div>
                    )}
                    <div
                      className={`flex items-center gap-1 mt-2 text-xs ${
                        field.match ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {field.match ? (
                        <Check className="w-3 h-3" aria-hidden="true" />
                      ) : (
                        <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                      )}
                      <span>{field.match ? 'Match' : 'Mismatch'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Required */}
          {hasMismatch && (
            <div
              className="p-4 rounded-lg bg-amber-50 border-l-4 border-amber-500"
              role="alert"
            >
              <h4 className="font-semibold text-amber-800">Action Required</h4>
              <p className="text-sm text-amber-700 mt-1">
                Discrepancies detected between factsheet and existing project data. Edit the
                values above and click &quot;Resolve & Save&quot; to update the project, or mark it as
                &quot;Not Eligible&quot; if this project should be excluded.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-4 rounded-lg bg-red-50 border-l-4 border-red-500" role="alert">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleMarkNonEligible}
              className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              disabled={isSubmitting}
            >
              <Ban className="w-4 h-4" />
              {isSubmitting ? 'Processing...' : 'Mark Non-Eligible'}
            </button>
            <button
              onClick={handleResolve}
              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              disabled={isSubmitting}
            >
              <Save className="w-4 h-4" />
              {isSubmitting ? 'Saving...' : 'Resolve & Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FactsheetComparisonModal;
